import { useEffect, useState, useMemo, useRef } from "react";
import { useLenis } from "lenis/react";
import Logo from "../../imgs/logo/personal_logo.svg";

function Navbar() {
  const [activeLink, setActiveLink] = useState("Home");
  const [mobileOpen, setMobileOpen] = useState(false);
  const isClickScrolling = useRef(false);
  const lenis = useLenis();

  const navItems = useMemo(
    () => [
      { href: "#home", name: "Home", id: "home" },
      { href: "#about", name: "About", id: "about" },
      { href: "#project", name: "Projects", id: "project" },
      { href: "#exp", name: "Experience", id: "exp" },
      { href: "#contact", name: "Contact", id: "contact" },
    ],
    [],
  );

  useEffect(() => {
    const handleScroll = () => {
      const sections = navItems.map((item) =>
        document.querySelector(item.href),
      );
      const scrollPosition = window.scrollY + 120;

      if (isClickScrolling.current) return;

      const isNearBottom =
        window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight - 100;

      if (isNearBottom) {
        setActiveLink(navItems[navItems.length - 1].name);
        return;
      }

      sections.forEach((section, index) => {
        if (section) {
          const sectionTop = section.offsetTop;
          const sectionHeight = section.offsetHeight;
          if (
            scrollPosition >= sectionTop &&
            scrollPosition < sectionTop + sectionHeight
          ) {
            setActiveLink(navItems[index].name);
          }
        }
      });
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [navItems]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const handleScrollTo = (id) => {
    const target = document.querySelector(id);
    if (target && lenis) {
      isClickScrolling.current = true;
      lenis.scrollTo(target, {
        onComplete: () => {
          isClickScrolling.current = false;
        },
      });
      setTimeout(() => {
        isClickScrolling.current = false;
      }, 1500);
    }
  };

  return (
    <>
      {/* ─── Top Navigation ─── */}
      <nav className='fixed top-0 left-0 z-50 w-full border-b border-line bg-background'>
        <div className='swiss-container flex h-20 items-center justify-between'>
          {/* Wordmark */}
          <button
            onClick={() => {
              setActiveLink("Home");
              handleScrollTo("#home");
            }}
            className='flex items-center gap-3'
            aria-label='Back to top'
          >
            <img
              src={Logo}
              alt='Zin Myo Win'
              className='h-8 w-8 brightness-0 invert'
            />
            <span className='hidden font-heading text-sm font-bold uppercase tracking-wider text-primary sm:block'>
              Zin&nbsp;Myo&nbsp;Win
            </span>
          </button>

          {/* Desktop Navigation Links */}
          <div className='hidden items-stretch md:flex'>
            {navItems.map((item, idx) => {
              const active = activeLink === item.name;
              return (
                <button
                  key={item.id}
                  onClick={() => {
                    setActiveLink(item.name);
                    handleScrollTo(item.href);
                  }}
                  className='group relative px-5 py-2'
                >
                  <span className='mr-2 font-body text-2xs font-medium tabular-nums text-primary/30'>
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                  <span
                    className={`font-body text-xs font-medium uppercase tracking-wider transition-colors ${
                      active
                        ? "text-accent"
                        : "text-primary/60 group-hover:text-primary"
                    }`}
                  >
                    {item.name}
                  </span>
                  <span
                    className={`absolute bottom-0 left-0 h-px w-full ${
                      active ? "bg-accent" : "bg-transparent"
                    }`}
                  />
                </button>
              );
            })}
          </div>

          {/* Mobile Menu Trigger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className='flex h-8 w-8 flex-col items-end justify-center gap-[6px] md:hidden'
            aria-label='Toggle menu'
          >
            <span
              className={`block h-0.5 bg-primary transition-all duration-200 ${
                mobileOpen ? "w-6 translate-y-[4px] rotate-45" : "w-6"
              }`}
            />
            <span
              className={`block h-0.5 bg-primary transition-all duration-200 ${
                mobileOpen ? "w-6 -translate-y-[4px] -rotate-45" : "w-4"
              }`}
            />
          </button>
        </div>
      </nav>

      {/* ─── Mobile Full-Screen Overlay ─── */}
      {mobileOpen && (
        <div
          className='fixed inset-0 z-40 flex flex-col justify-center'
          style={{ backgroundColor: "var(--background)" }}
        >
          <div className='absolute top-7 right-6 font-body text-2xs font-medium uppercase tracking-widest text-primary/30'>
            Menu / 05
          </div>

          <nav className='swiss-container flex flex-col border-t border-line'>
            {navItems.map((item, idx) => {
              const active = activeLink === item.name;
              return (
                <button
                  key={item.id}
                  onClick={() => {
                    setActiveLink(item.name);
                    handleScrollTo(item.href);
                    setMobileOpen(false);
                  }}
                  className='flex items-baseline gap-5 border-b border-line py-5 text-left'
                >
                  <span className='font-body text-xs font-medium tabular-nums text-primary/30'>
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                  <span
                    className={`font-heading text-4xl font-bold uppercase tracking-tight ${
                      active ? "text-accent" : "text-primary"
                    }`}
                  >
                    {item.name}
                  </span>
                </button>
              );
            })}
          </nav>

          <div className='swiss-container mt-10 flex items-center justify-between'>
            <span className='font-body text-2xs uppercase tracking-widest text-primary/30'>
              Portfolio 2026
            </span>
            <span className='font-body text-2xs uppercase tracking-widest text-primary/30'>
              Zin Myo Win
            </span>
          </div>
        </div>
      )}
    </>
  );
}

export default Navbar;
